import React from "react";
import { useLang } from "@/contexts/LanguageContext";
import { Info, Zap, Scale, TrendingUp } from "lucide-react";
import { CONDITIONS } from "@/lib/catalogMeta";

const URGENCY_META = {
  fast: { icon: <Zap size={14} /> },
  balanced: { icon: <Scale size={14} /> },
  max_profit: { icon: <TrendingUp size={14} /> },
};

export const ConditionPanel = ({ form, update }) => {
  const { t } = useLang();
  const isSalvage = form.condition === "damaged" || form.condition === "for_parts";

  return (
    <div className="lg:col-span-5 bg-[#F5F5F5] border border-black p-8">
      <label className="label-rv">{t.valuator.condition}</label>
      <div data-testid="form-condition" className="grid grid-cols-2 gap-2 mt-2">
        {CONDITIONS.map((c) => (
          <button
            type="button"
            key={c}
            data-testid={`cond-${c}`}
            onClick={() => update("condition", c)}
            className={`text-xs font-mono font-bold uppercase tracking-wider py-3 lg:py-2 min-h-[44px] lg:min-h-0 border ${form.condition === c ? (c === "damaged" || c === "for_parts" ? "bg-[#FF3B30] text-white border-[#FF3B30]" : "bg-black text-white border-black") : "bg-white text-black border-black/40 hover:border-black"}`}
          >
            {t.valuator.conditions[c]}
          </button>
        ))}
      </div>
      <div data-testid="condition-help" className="mt-3 bg-white border border-black/20 p-3 flex gap-2">
        <Info size={14} className={`mt-0.5 flex-shrink-0 ${isSalvage ? "text-[#FF3B30]" : "text-[#002FA7]"}`} />
        <div>
          <div className={`font-mono text-[10px] font-bold uppercase tracking-widest ${isSalvage ? "text-[#FF3B30]" : "text-[#002FA7]"}`}>{t.valuator.conditions[form.condition]}</div>
          <div className="text-xs text-[#4A4A4A] mt-1 leading-relaxed">{t.valuator.condition_desc[form.condition]}</div>
        </div>
      </div>

      <div className="mt-6">
        <label className="label-rv">{t.valuator.purchase_type_q}</label>
        <div data-testid="form-purchase-type" className="grid grid-cols-2 gap-2 mt-2">
          {["new", "used"].map((pt) => (
            <button
              type="button"
              key={pt}
              data-testid={`purchase-type-${pt}`}
              onClick={() => update("purchase_type", pt)}
              className={`text-xs font-mono font-bold uppercase tracking-wider py-3 lg:py-2.5 min-h-[44px] border ${form.purchase_type === pt ? "bg-black text-white border-black" : "bg-white text-black border-black/40 hover:border-black"}`}
            >
              {pt === "new" ? t.valuator.purchase_type_new : t.valuator.purchase_type_used}
            </button>
          ))}
        </div>

        <div className="mt-4">
          <label className="label-rv">{form.purchase_type === "used" ? t.valuator.owned_time_used : t.valuator.owned_time_new}</label>
          <div className="flex gap-2">
            <input
              data-testid="form-owned-time"
              type="number"
              min="0"
              className="input-rv flex-1 min-w-0 text-gray-900"
              value={form.owned_time}
              onChange={(e) => update("owned_time", e.target.value)}
              placeholder="12"
            />
            <select data-testid="form-owned-unit" className="input-rv w-28 flex-none" style={{ flex: "0 0 7rem" }} value={form.owned_unit} onChange={(e) => update("owned_unit", e.target.value)}>
              <option value="months">{t.valuator.unit_months}</option>
              <option value="years">{t.valuator.unit_years}</option>
            </select>
          </div>
        </div>

        {form.purchase_type === "used" && (
          <div className="mt-4" data-testid="age-at-purchase-block">
            <label className="label-rv">{t.valuator.age_at_purchase_q}</label>
            <div className="flex gap-2">
              <input
                data-testid="form-age-at-purchase"
                type="number"
                min="0"
                className="input-rv flex-1 min-w-0 text-gray-900"
                value={form.age_at_purchase_time}
                onChange={(e) => update("age_at_purchase_time", e.target.value)}
                placeholder="2"
              />
              <select data-testid="form-age-at-purchase-unit" className="input-rv w-28 flex-none" style={{ flex: "0 0 7rem" }} value={form.age_at_purchase_unit} onChange={(e) => update("age_at_purchase_unit", e.target.value)}>
                <option value="months">{t.valuator.unit_months}</option>
                <option value="years">{t.valuator.unit_years}</option>
              </select>
            </div>
            <p className="text-[11px] text-[#737373] mt-1">{t.valuator.age_at_purchase_help}</p>
          </div>
        )}
      </div>

      <div className="mt-6">
        <label className="label-rv">{t.valuator.urgency}</label>
        <div data-testid="form-urgency" className="space-y-2 mt-2">
          {["fast", "balanced", "max_profit"].map((u) => (
            <button
              type="button"
              key={u}
              data-testid={`urgency-${u}`}
              onClick={() => update("urgency", u)}
              className={`w-full flex items-center gap-2 text-xs font-mono font-bold uppercase tracking-wider py-3.5 lg:py-2.5 min-h-[48px] lg:min-h-0 px-3 border text-left ${form.urgency === u ? "bg-[#002FA7] text-white border-[#002FA7]" : "bg-white text-black border-black/40 hover:border-black"}`}
            >
              {URGENCY_META[u].icon} {t.valuator.urgencies[u]}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

export default ConditionPanel;
